import React from "react"
import PropTypes from "prop-types"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    MenuItem,
    FormControlLabel,
    Switch,
    Divider,
    Alert
} from "@mui/material"
import LectureResourcesEditor from "./LectureResourcesEditor"

const NO_CHAPTER = ""

const emptyLecture = {
    title: "",
    description: "",
    video_url: "",
    duration: "",
    sequence: "",
    chapter_id: NO_CHAPTER,
    is_preview: false
}

/**
 * Create / edit form for a lecture. Controlled entirely by the parent so the
 * same dialog serves both operations.
 *
 * `lecture` is kept live by the parent (it is re-read from the lecture list
 * after every refetch) so the resources editor reflects each change; the form
 * itself is only reset when another lecture is opened. `defaultChapterId`
 * pre-selects the chapter when adding a lecture from inside one.
 */
export default function LectureFormDialog({
    open,
    lecture,
    course,
    chapters = [],
    defaultChapterId = null,
    saving,
    error,
    onClose,
    onSubmit
}) {
    const [form, setForm] = React.useState(emptyLecture)
    const [errors, setErrors] = React.useState({})

    /* Read the latest props without making every refetch reset the form. */
    const latest = React.useRef({ lecture, defaultChapterId })
    latest.current = { lecture, defaultChapterId }
    const lectureId = lecture?.lecture_id

    React.useEffect(() => {
        if (!open) return
        const { lecture: current, defaultChapterId: chapterId } = latest.current
        setErrors({})
        setForm(
            current
                ? {
                    title: current.title || "",
                    description: current.description || "",
                    video_url: current.video_url || "",
                    duration: current.duration || "",
                    sequence: current.sequence || "",
                    chapter_id:
                        current.chapter_id === null ||
                        current.chapter_id === undefined
                            ? NO_CHAPTER
                            : String(current.chapter_id),
                    is_preview: Boolean(current.is_preview)
                }
                : {
                    ...emptyLecture,
                    chapter_id:
                        chapterId === null || chapterId === undefined
                            ? NO_CHAPTER
                            : String(chapterId)
                }
        )
    }, [open, lectureId])

    const setField = (field) => (event) =>
        setForm((prev) => ({ ...prev, [field]: event.target.value }))

    const handleSubmit = () => {
        const nextErrors = {}
        if (!form.title.trim()) nextErrors.title = "Title is required"
        if (!form.video_url.trim()) {
            nextErrors.video_url = "Video URL is required"
        }

        setErrors(nextErrors)
        if (Object.keys(nextErrors).length) return

        const chapter_id =
            form.chapter_id === NO_CHAPTER ? null : Number(form.chapter_id)

        /*
         * The position is relative to the chapter. When the lecture moves to
         * another chapter with its old position untouched, leave it out so
         * the server appends it to the end of the new chapter.
         */
        const movedChapter =
            lecture && (lecture.chapter_id ?? null) !== chapter_id
        const keptOldPosition =
            lecture && String(form.sequence) === String(lecture.sequence || "")

        onSubmit({
            ...form,
            chapter_id,
            is_preview: Boolean(form.is_preview),
            sequence:
                form.sequence && !(movedChapter && keptOldPosition)
                    ? Number(form.sequence)
                    : undefined
        })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            /* Full screen below sm so the form is usable on a phone. */
            sx={{
                "& .MuiDialog-paper": {
                    m: { xs: 0, sm: 4 },
                    width: { xs: "100%", sm: "auto" },
                    maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
                    height: { xs: "100%", sm: "auto" },
                    borderRadius: { xs: 0, sm: 2 }
                }
            }}
        >
            <DialogTitle>
                {lecture ? "Edit lecture" : "Add a lecture"}
                {course?.title && (
                    <span className="mt-0.5 block text-xs font-normal text-slate-500">
                        {course.title}
                    </span>
                )}
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2} sx={{ pt: 1 }}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Lecture title"
                            value={form.title}
                            onChange={setField("title")}
                            error={Boolean(errors.title)}
                            helperText={errors.title}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Chapter"
                            value={form.chapter_id}
                            onChange={setField("chapter_id")}
                        >
                            <MenuItem value={NO_CHAPTER}>
                                <em>No chapter</em>
                            </MenuItem>
                            {chapters.map((chapter) => (
                                <MenuItem
                                    key={chapter.chapter_id}
                                    value={String(chapter.chapter_id)}
                                >
                                    {chapter.title}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Duration"
                            placeholder="12:40"
                            value={form.duration}
                            onChange={setField("duration")}
                        />
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Position in chapter"
                            value={form.sequence}
                            onChange={setField("sequence")}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Video URL"
                            placeholder="YouTube / Vimeo link, or a direct .mp4 URL"
                            value={form.video_url}
                            onChange={setField("video_url")}
                            error={Boolean(errors.video_url)}
                            helperText={
                                errors.video_url ||
                                "YouTube and Vimeo links are embedded; any other link plays in the native player."
                            }
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            size="small"
                            label="Description"
                            value={form.description}
                            onChange={setField("description")}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.is_preview}
                                    onChange={(event) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            is_preview: event.target.checked
                                        }))
                                    }
                                />
                            }
                            label={
                                <span className="text-sm">
                                    Free preview
                                    <span className="block text-xs text-slate-500">
                                        Students can watch this lecture before
                                        buying the course.
                                    </span>
                                </span>
                            }
                        />
                    </Grid>

                    {error && (
                        <Grid size={{ xs: 12 }}>
                            <Alert severity="error">{error}</Alert>
                        </Grid>
                    )}

                    {/* Resources can only hang off a saved lecture. */}
                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ mb: 2 }} />
                        {lecture ? (
                            <LectureResourcesEditor lecture={lecture} />
                        ) : (
                            <p className="text-xs text-slate-500">
                                Save the lecture first, then edit it to attach
                                PDFs and links.
                            </p>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={saving}>
                    {lecture ? "Close" : "Cancel"}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {lecture ? "Save changes" : "Add lecture"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

LectureFormDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    lecture: PropTypes.object,
    course: PropTypes.object,
    chapters: PropTypes.array,
    defaultChapterId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    saving: PropTypes.bool,
    error: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired
}
